import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, ShieldAlert, LogOut, Monitor, Clock, MapPin } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

// Estrae un hint breve dal user-agent (browser/OS) senza dipendenze esterne.
function shortUA(ua = "") {
  if (!ua) return "—";
  const lower = ua.toLowerCase();
  let browser = "Browser";
  if (lower.includes("edg/")) browser = "Edge";
  else if (lower.includes("chrome/")) browser = "Chrome";
  else if (lower.includes("firefox/")) browser = "Firefox";
  else if (lower.includes("safari/") && !lower.includes("chrome/")) browser = "Safari";
  else if (lower.includes("curl")) browser = "curl";
  let os = "";
  if (lower.includes("iphone") || lower.includes("ipad")) os = "iOS";
  else if (lower.includes("android")) os = "Android";
  else if (lower.includes("windows")) os = "Windows";
  else if (lower.includes("mac os")) os = "macOS";
  else if (lower.includes("linux")) os = "Linux";
  return os ? `${browser} · ${os}` : browser;
}

export default function LoginHistoryModal({ user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${user.id}/login-history?limit=50`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento storico login");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const revoke = async () => {
    if (!window.confirm("Revocare tutte le sessioni attive? L'utente dovrà rifare il login.")) return;
    setRevoking(true);
    try {
      const { data: res } = await api.post(`/users/${user.id}/revoke-sessions`);
      toast.success(`Revocate ${res.revoked_count} sessioni`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore revoca");
    } finally {
      setRevoking(false);
    }
  };

  const alert = data && data.distinct_ips_24h > 3;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="login-history-modal">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Sessioni & IP</h3>
            <p className="text-xs text-slate-500 mt-0.5">{user.full_name || user.email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="close-login-history">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && <div className="text-sm text-slate-500">Caricamento…</div>}
          {!loading && data && (
            <>
              {/* Alert IP condivisione */}
              <div
                className={`rounded-lg p-3 flex items-start gap-3 border ${alert ? "bg-red-50 border-red-200 text-red-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}
                data-testid="ip-alert-banner"
              >
                <ShieldAlert size={18} className="mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-semibold">
                    {alert
                      ? `⚠ Possibile condivisione credenziali: ${data.distinct_ips_24h} IP diversi in 24h`
                      : `Nessun segnale sospetto: ${data.distinct_ips_24h} IP nelle ultime 24h`}
                  </div>
                  {data.ips_24h && data.ips_24h.length > 0 && (
                    <div className="text-xs mt-1 font-mono opacity-80 break-all">
                      {data.ips_24h.join(" · ")}
                    </div>
                  )}
                </div>
              </div>

              {/* Sessioni attive */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-slate-800">
                    Sessioni attive ({data.active_sessions?.length || 0}/2)
                  </h4>
                  {data.active_sessions?.length > 0 && (
                    <button
                      onClick={revoke}
                      disabled={revoking}
                      className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 flex items-center gap-1.5 disabled:opacity-50"
                      data-testid="revoke-sessions-btn"
                    >
                      <LogOut size={13} /> {revoking ? "Revoco…" : "Revoca tutte"}
                    </button>
                  )}
                </div>
                {(!data.active_sessions || data.active_sessions.length === 0) ? (
                  <div className="text-xs text-slate-500 italic">Nessuna sessione attiva</div>
                ) : (
                  <ul className="space-y-2">
                    {data.active_sessions.map((s) => (
                      <li key={s.sid} className="p-3 rounded-lg border border-slate-200 bg-slate-50/50 text-sm">
                        <div className="flex items-center gap-2 text-slate-700">
                          <Monitor size={14} className="text-blue-600" />
                          <span className="font-medium">{shortUA(s.user_agent)}</span>
                          <span className="text-slate-400">·</span>
                          <MapPin size={12} className="text-slate-500" />
                          <span className="font-mono text-xs text-slate-600">{s.ip || "—"}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                          <Clock size={11} /> Login: {fmtDateTime(s.created_at)} · Ultima attività: {fmtDateTime(s.last_seen_at)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Storico completo */}
              <div>
                <h4 className="text-sm font-semibold text-slate-800 mb-2">
                  Storico login (ultimi {data.history?.length || 0})
                </h4>
                {(!data.history || data.history.length === 0) ? (
                  <div className="text-xs text-slate-500 italic">Nessun accesso registrato</div>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 text-slate-700">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Data/Ora</th>
                          <th className="text-left px-3 py-2 font-medium">IP</th>
                          <th className="text-left px-3 py-2 font-medium">Dispositivo</th>
                          <th className="text-left px-3 py-2 font-medium">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.history.map((h) => (
                          <tr key={h.sid} className="border-t border-slate-100">
                            <td className="px-3 py-2 whitespace-nowrap">{fmtDateTime(h.created_at)}</td>
                            <td className="px-3 py-2 font-mono">{h.ip || "—"}</td>
                            <td className="px-3 py-2 text-slate-600">{shortUA(h.user_agent)}</td>
                            <td className="px-3 py-2 text-slate-500">
                              {h.evicted_sids && h.evicted_sids.length > 0 ? (
                                <span className="text-amber-700">disconnessa sessione precedente</span>
                              ) : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
